/**
 * Sign-in. Google and GitHub only — no passwords to reset, phish or store.
 *
 * This page used to have two faces, because Archis was invitation-only: arriving with
 * an invitation token it explained whose invitation it was, and arriving without one
 * it explained that there was nothing to sign up for. Signing up is the product's
 * front door now — an account with nothing behind it gets Archis Free — so the page has
 * one job and does it.
 *
 * What survives from that design is the sentence about providers. Firebase gives
 * each provider its own identity, and the backend reconciles them by verified email
 * so one person does not end up with two accounts. Somebody who used
 * GitHub in March and Google in June needs to know that before they wonder where
 * their licence went.
 *
 * The gate that remains is server-side, as it always was: a deployment can still
 * close signups (CHI_INVITE_ONLY_SIGNUPS), and the API refuses to create an account
 * for an address it has no invitation for. This page does not try to predict that —
 * the refusal comes back as an error, written for the person reading it.
 */

import { useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'

import { useSession } from '@/app/providers'
import { SignInError, signInWithSSO, type ProviderId } from '@/app/firebase'
import { discoverSSO } from '@/shared/api/hooks'
import { capture } from '@/shared/lib/analytics'
import { Button, Field, Label, Notice } from '@/shared/ui/primitives'
import { Masthead } from '@/shared/ui/Chrome'

export function SignIn() {
  const { user, ready, signIn } = useSession()
  const [params] = useSearchParams()
  const [pending, setPending] = useState<ProviderId | 'sso' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ssoOpen, setSSOOpen] = useState(false)
  const [ssoEmail, setSSOEmail] = useState('')

  const next = params.get('next') ?? '/account'
  const userCode = params.get('user_code')

  // Already signed in: honour the `next` target rather than showing a sign-in form
  // to somebody who is signed in.
  if (ready && user) {
    return <Navigate to={userCode ? `/cli/activate?user_code=${userCode}` : next} replace />
  }

  async function attempt(provider: ProviderId) {
    setPending(provider)
    setError(null)
    capture('sign_in_started', { provider })
    try {
      await signIn(provider)
      capture('sign_in_completed', { provider })
    } catch (cause) {
      capture('sign_in_failed', {
        provider,
        reason: cause instanceof SignInError ? cause.message : 'unknown',
        code: cause instanceof SignInError ? cause.code : 'unknown',
      })
      setError(cause instanceof SignInError ? cause.message : 'Sign-in failed. Please try again.')
    } finally {
      setPending(null)
    }
  }

  /**
   * Company SSO: ask the backend which provider the address's domain routes
   * through, then run that provider's popup. The session listener picks the
   * result up exactly as it does for the social providers.
   */
  async function attemptSSO(event: React.FormEvent) {
    event.preventDefault()
    setPending('sso')
    setError(null)
    capture('sign_in_started', { provider: 'sso' })
    try {
      const discovery = await discoverSSO(ssoEmail.trim())
      if (!discovery.sso || !discovery.provider_id) {
        capture('sign_in_failed', { provider: 'sso', reason: 'not_configured', code: 'sso/not-configured' })
        setError('No single sign-on is configured for that email domain. Ask your workspace admin, or use GitHub / Google.')
        return
      }
      await signInWithSSO(discovery.provider_id)
      capture('sign_in_completed', { provider: 'sso' })
    } catch (cause) {
      capture('sign_in_failed', {
        provider: 'sso',
        reason: cause instanceof SignInError ? cause.message : 'unknown',
        code: cause instanceof SignInError ? cause.code : 'unknown',
      })
      setError(cause instanceof SignInError ? cause.message : 'SSO sign-in failed. Please try again.')
    } finally {
      setPending(null)
    }
  }

  return (
    <>
      <Masthead />
      <main id="main"
        className="shell"
        style={{ display: 'grid', placeItems: 'center', minHeight: 'calc(100vh - 60px)' }}
      >
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div className="panel panel--flush">
            <div className="panel__head">
              <Label index="00">Sign in</Label>
            </div>

            <div className="panel__body stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
              <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                <h1 style={{ fontSize: '1.5rem', margin: 0 }}>
                  {userCode ? 'Sign in to activate the CLI' : 'Sign in, or create your free account'}
                </h1>
                <p className="mono-note">
                  {userCode
                    ? `Activating the CLI · code ${userCode}`
                    : 'One account for your licence, devices and billing. Archis Free needs no card.'}
                </p>
              </div>

              {error && <Notice tone="error">{error}</Notice>}

              <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
                <Button
                  block
                  size="lg"
                  variant="primary"
                  loading={pending === 'github'}
                  disabled={pending !== null}
                  onClick={() => void attempt('github')}
                >
                  Continue with GitHub
                </Button>
                <Button
                  block
                  size="lg"
                  loading={pending === 'google'}
                  disabled={pending !== null}
                  onClick={() => void attempt('google')}
                >
                  Continue with Google
                </Button>
              </div>

              {ssoOpen ? (
                <form className="stack" style={{ ['--gap' as string]: 'var(--s3)' }} onSubmit={(event) => void attemptSSO(event)}>
                  <Field label="Work email" hint="We route you to your company's identity provider.">
                    <input
                      className="input"
                      type="email"
                      required
                      autoFocus
                      value={ssoEmail}
                      onChange={(event) => setSSOEmail(event.target.value)}
                      placeholder="you@company.com"
                    />
                  </Field>
                  <Button
                    type="submit"
                    block
                    loading={pending === 'sso'}
                    disabled={pending !== null && pending !== 'sso'}
                  >
                    Continue with SSO
                  </Button>
                </form>
              ) : (
                <button
                  type="button"
                  className="mono-note signal"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                  onClick={() => setSSOOpen(true)}
                >
                  Company account? Sign in with SSO →
                </button>
              )}

              <p className="mono-note">
                We ask GitHub or Google for your name and email address only, never for access to
                your repositories. Used the other provider last time? The same verified email reaches
                the same account.
              </p>
            </div>

            <div className="panel__foot mono-note">
              By continuing you accept the{' '}
              <Link to="/terms" className="signal">
                Terms &amp; EULA
              </Link>{' '}
              and the{' '}
              <Link to="/privacy" className="signal">
                Privacy Policy
              </Link>
              .
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
